/**
 * When to re-acquire the local camera, platform-free.
 *
 * A sender's camera can die while the call is on screen: the page was
 * backgrounded and the browser suspended the camera, a USB camera was pulled,
 * or (native) the capture never restarted after the app came back. The call
 * then carries a frozen or black picture of the sender, and nothing reopened
 * the camera (2026-09-28, Kuwait demo freeze follow-up).
 *
 * The platform samples the camera every few seconds; this decides. A camera
 * the person turned off, a screen share, or a hidden page is never touched.
 * Each dead track gets one re-acquire; a call gets at most
 * MAX_CAMERA_REACQUIRES_PER_CALL.
 */
export const MAX_CAMERA_REACQUIRES_PER_CALL = 3;
/** An ended track goes after a moment, so the device-list handler can replace a pulled camera first. */
export const CAMERA_ENDED_GRACE_MS = 1500;
/**
 * Decides, sample by sample, when the local camera needs reopening.
 *
 * @param {Object} options - Policy options.
 * @param {number} options.silentGraceMs - How long a silent camera is given
 * (a camera that just restarted takes a moment to deliver).
 * @returns {Object}
 */
export function createCameraReacquirePolicy({ silentGraceMs }) {
    const tried = new WeakSet();
    let deadSince;
    let deadTrack;
    let reacquires = 0;
    return {
        observe(probe) {
            const { at, ended, isCamera, silent, track, userMuted, visible } = probe;
            if (!track || !isCamera || userMuted || !visible || !(ended || silent)) {
                deadSince = undefined;
                deadTrack = undefined;
                return { reacquire: false };
            }
            if (deadTrack !== track) {
                deadTrack = track;
                deadSince = at;
            }
            const since = deadSince ?? at;
            if (at - since < (ended ? CAMERA_ENDED_GRACE_MS : silentGraceMs)
                || tried.has(track)
                || reacquires >= MAX_CAMERA_REACQUIRES_PER_CALL) {
                return { reacquire: false };
            }
            tried.add(track);
            reacquires += 1;
            return {
                reacquire: true,
                reason: ended ? 'ended' : 'silent'
            };
        }
    };
}
